import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import CustomTypeahead from '../../components/CustomTypeahead';
import {
	Button,
	Checkbox,
	FormField,
	RequiredMark,
	Section,
	Select,
	controlClass,
	textareaClass,
} from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import { resolveBillCurrency } from '../Balance/balanceTotals';
import BillService from '../../services/bill.service';
import AssetService from '../../services/asset.service';
import AccountService from '../../services/account.service';
import BankAccountService from '../../services/bankAccount.service';
import { getError } from '../../utils';
import { Store } from '../../Store';
import { useLabels } from '../../hooks/useLabels';
import BillPayersEditor from '../../components/BillPayersEditor/BillPayersEditor';
import { validatePayers } from '../../utils/billPayers';
import { isBillPayersEnabled, isBillEstimatesEnabled, isBillSourcesEnabled } from '../../utils/featureFlags';

const BILL_TYPES = ['Periodico', 'Eventual'];
const CURRENCIES = ['USD', 'UYU'];

const MONTH_OPTIONS = [
	{ value: '1', label: 'Enero' },
	{ value: '2', label: 'Febrero' },
	{ value: '3', label: 'Marzo' },
	{ value: '4', label: 'Abril' },
	{ value: '5', label: 'Mayo' },
	{ value: '6', label: 'Junio' },
	{ value: '7', label: 'Julio' },
	{ value: '8', label: 'Agosto' },
	{ value: '9', label: 'Septiembre' },
	{ value: '10', label: 'Octubre' },
	{ value: '11', label: 'Noviembre' },
	{ value: '12', label: 'Diciembre' },
];

const PERIODICITY_OPTIONS = [
	{ value: '1', label: 'Mensual' },
	{ value: '3', label: 'Trimestral' },
	{ value: '4', label: 'Cuatrimestral' },
	{ value: '6', label: 'Semestral' },
	{ value: '12', label: 'Anual' },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => {
	const year = currentYear - 5 + i;
	return { value: String(year), label: String(year) };
});

const emptyForm = {
	type: 'Periodico',
	name: '',
	folder: '',
	amount: '',
	currency: '',
	observations: '',
	account: null,
	asset: null,
	partyType: '',
	bankAccount: null,
	startDate: null,
	startMonth: '',
	startYear: '',
	periodicity: '',
	expirationDate: null,
	billingDay: '',
	generateRetroactive: true,
	// SHOW_BILL_PAYERS / SHOW_BILL_ESTIMATES — only sent while their flag is on.
	payers: [],
	amountMode: 'fixed',
};

const payersToForm = (payers) =>
	(payers || []).map((p) => ({ account: p.account?._id || p.account || null, percentage: p.percentage !== undefined ? String(p.percentage) : '' }));

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_DONE':
		return { ...state, loading: false };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	default:
		return state;
	}
};

// Day clamping so e.g. month=1 (Feb) + day=31 doesn't roll into March.
const createDate = (targetYear, targetMonth, targetDay) => {
	const d = new Date(targetYear, targetMonth, targetDay);
	if (d.getDate() !== targetDay) {
		d.setDate(0);
	}
	return d;
};

const billToForm = (bill) => {
	if (!bill || Object.keys(bill).length === 0) {
		return { ...emptyForm };
	}
	const form = {
		...emptyForm,
		...bill,
		asset: bill.asset?._id || bill.asset || null,
		// Bills created from an asset (AssetBills.jsx -> NewBillsNew.jsx) arrive as
		// initialData={{ asset }} with no account. The asset typeahead's onChange —
		// the only other code that derives account from asset — is disabled by
		// isAssetLocked, so the fallback has to happen here.
		account: bill.account?._id || bill.account || bill.asset?.owner?._id || bill.asset?.owner || null,
		name: bill.type === 'Periodico' && bill.billTemplate?.name ? bill.billTemplate.name : bill.name || '',
		generateRetroactive: false,
		periodicity: bill.periodicity ? String(bill.periodicity) : '',
		partyType: bill.partyType || '',
		bankAccount: bill.bankAccount?._id || bill.bankAccount || null,
		currency: resolveBillCurrency(bill) || '',
		payers: payersToForm(bill.payers || bill.billTemplate?.payers),
		amountMode: bill.billTemplate?.amountMode || bill.amountMode || 'fixed',
	};

	if (bill.startDate) {
		if (bill.type === 'Periodico') {
			const parsedStartDate = bill.startDate instanceof Date ? bill.startDate : new Date(bill.startDate);
			if (!isNaN(parsedStartDate.getTime())) {
				form.startMonth = String(parsedStartDate.getMonth() + 1);
				form.startYear = String(parsedStartDate.getFullYear());
			}
			form.startDate = null;
		} else {
			form.startDate = bill.startDate instanceof Date ? bill.startDate : new Date(bill.startDate);
		}
	} else {
		form.startDate = null;
	}

	form.expirationDate = bill.expirationDate
		? (bill.expirationDate instanceof Date ? bill.expirationDate : new Date(bill.expirationDate))
		: null;

	return form;
};

const toDateInputValue = (value) => {
	if (!value) return '';
	const d = value instanceof Date ? value : new Date(value);
	if (isNaN(d.getTime())) return '';
	return d.toISOString().slice(0, 10);
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const BillsEditor = ({ mode = 'create', billId = null, initialData = null, scope = 'full', typeLocked = false, onCancel, onSaved, cancelLabel = 'Cancelar', saveLabel }) => {
	const { t } = useLabels();
	const { state } = useContext(Store);
	const { userInfo } = state;
	const isEditing = mode === 'edit';
	// 'instance': this generated bill only — never its BillTemplate, and no
	// backfill. 'template': the recurring gasto, without touching bills already
	// generated. 'full' keeps the original behaviour (PUT /bills/:id).
	const isInstanceScope = scope === 'instance';
	const isTemplateScope = scope === 'template';
	const isTypeLocked = isEditing || typeLocked || isInstanceScope || isTemplateScope;
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();

	const [{ loading, saving, error }, dispatch] = useReducer(reducer, {
		loading: true,
		saving: false,
		error: '',
	});

	const [form, setForm] = useState(() => billToForm(initialData));
	const [assets, setAssets] = useState([]);
	const [accounts, setAccounts] = useState([]);
	const [bankAccounts, setBankAccounts] = useState([]);
	const [fieldErrors, setFieldErrors] = useState({});
	const [preloadedAsset, setPreloadedAsset] = useState(initialData?.asset && typeof initialData.asset === 'object' ? initialData.asset : null);

	const isAssetLocked = !!(initialData && initialData.asset);

	// Ids injected via ?accountId=/?propertyId=/?bankAccountId= after a
	// "crear nueva..." round trip. The asset param is 'propertyId', not
	// 'assetId' — that's what NewAsset.jsx actually sends (see B1 in
	// 17-backlog-minor-defects.md); matching it here, not renaming it.
	const injectedAccountId = !isAssetLocked ? searchParams.get('accountId') : null;
	const injectedAssetId = !isAssetLocked ? searchParams.get('propertyId') : null;
	const injectedBankAccountId = searchParams.get('bankAccountId');
	const consumedAccountId = useRef(null);
	const consumedAssetId = useRef(null);
	const consumedBankAccountId = useRef(null);

	// Keep the URL mirroring the current selection so a manual pick or clear
	// doesn't leave a stale id from an earlier round trip to be re-injected.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	const periodicityOptions = useMemo(() => {
		if (userInfo && (userInfo.email === 'support@gotasoft.io' || userInfo.email === 'admin@gotaccount.com')) {
			return [...PERIODICITY_OPTIONS, { value: '999', label: 'Cada 2 minutos' }];
		}
		return PERIODICITY_OPTIONS;
	}, [userInfo]);

	useEffect(() => {
		let isCurrent = true;

		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const requests = [AssetService.list({ slim: true }), AccountService.listWithAssets(), BankAccountService.list()];
				if (isEditing && billId && !initialData) {
					requests.unshift(BillService.getDetails(billId));
				}
				const responses = await Promise.all(requests);
				if (!isCurrent) {
					return;
				}

				let assetsRes, accountsRes, bankAccountsRes;
				if (isEditing && billId && !initialData) {
					const [billRes, aRes, accRes, baRes] = responses;
					assetsRes = aRes;
					accountsRes = accRes;
					bankAccountsRes = baRes;
					setForm(billToForm(billRes.data));
				} else {
					[assetsRes, accountsRes, bankAccountsRes] = responses;
					setForm(billToForm(initialData));
				}

				const aOptions = assetsRes?.data || [];
				const accOptions = accountsRes?.data || [];
				const baOptions = bankAccountsRes?.data || [];
				setAssets(aOptions);
				setAccounts(accOptions);
				setBankAccounts(baOptions);

				if (initialData?.asset && typeof initialData.asset !== 'object') {
					const matchingAsset = aOptions.find((a) => a._id === initialData.asset);
					if (matchingAsset) {
						setPreloadedAsset(matchingAsset);
					}
				}

				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				const message = getError(ex);
				if (isCurrent) {
					dispatch({ type: 'FETCH_FAIL', payload: message });
				}
				toast.error(isEditing ? 'No se pudo obtener gasto' : t('msg.listLoadFailed'));
			} finally {
				if (isCurrent) {
					dispatch({ type: 'FETCH_DONE' });
				}
			}
		};

		fetchData();
		return () => {
			isCurrent = false;
		};
	}, [billId, isEditing]);

	// If `asset` wasn't populated on the bill, backfill currency once assets load.
	useEffect(() => {
		if (!isEditing || assets.length === 0) return;
		setForm((prev) => {
			if (prev.currency || !prev.asset) return prev;
			const assetDoc = assets.find((a) => String(a._id) === String(prev.asset));
			const c = assetDoc?.currency;
			if (!c) return prev;
			return { ...prev, currency: String(c).toUpperCase() };
		});
	}, [isEditing, assets]);

	// Auto-select an account created via the "crear nueva cuenta" link, which
	// returns here with ?accountId=<id>. accounts is already refetched fresh
	// on mount (see fetchData above), so find-in-list is enough. form.account
	// holds an id, not an object — see billToForm above.
	useEffect(() => {
		if (!injectedAccountId || consumedAccountId.current === injectedAccountId) return;
		const injectedAccount = accounts.find((a) => a._id === injectedAccountId);
		if (!injectedAccount) return;
		setForm((prev) => ({ ...prev, account: injectedAccount._id }));
		clearFieldErrors(['account']);
		consumedAccountId.current = injectedAccountId;
	}, [accounts, injectedAccountId]);

	// Auto-select an asset created via the "crear nuevo activo" link, same
	// round trip as the account handling above.
	useEffect(() => {
		if (!injectedAssetId || consumedAssetId.current === injectedAssetId) return;
		const injectedAsset = assets.find((a) => a._id === injectedAssetId);
		if (!injectedAsset) return;
		const ownerId = injectedAsset.owner?._id || injectedAsset.owner;
		setForm((prev) => ({ ...prev, asset: injectedAsset._id, account: ownerId || prev.account }));
		clearFieldErrors(['asset']);
		consumedAssetId.current = injectedAssetId;
	}, [assets, injectedAssetId]);

	// Auto-select a bank account created via the "crear nueva cuenta
	// bancaria" link, same round trip as above.
	useEffect(() => {
		if (!injectedBankAccountId || consumedBankAccountId.current === injectedBankAccountId) return;
		const injectedBankAccount = bankAccounts.find((ba) => ba._id === injectedBankAccountId);
		if (!injectedBankAccount) return;
		setForm((prev) => ({ ...prev, bankAccount: injectedBankAccount._id }));
		consumedBankAccountId.current = injectedBankAccountId;
	}, [bankAccounts, injectedBankAccountId]);

	const assetOptions = useMemo(() => {
		if (!form.account) return assets;
		return assets.filter((asset) => {
			const ownerId = asset.owner?._id || asset.owner;
			return ownerId === form.account;
		});
	}, [assets, form.account]);

	const updateField = (field, value) => {
		setForm((prevForm) => ({ ...prevForm, [field]: value }));
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const clearFieldErrors = (fields) => {
		setFieldErrors((prevErrors) => {
			if (!fields.some((field) => prevErrors[field])) return prevErrors;
			const nextErrors = { ...prevErrors };
			fields.forEach((field) => delete nextErrors[field]);
			return nextErrors;
		});
	};

	const handleTypeChange = (type) => {
		setForm((prevForm) => ({ ...prevForm, type }));
		clearFieldErrors(['startDate', 'expirationDate', 'startMonth', 'startYear', 'periodicity', 'billingDay']);
	};

	const handleAccountChange = (accountId) => {
		setForm((prevForm) => {
			if (accountId === prevForm.account) return prevForm;
			let nextAsset = prevForm.asset;
			if (nextAsset) {
				const currentAsset = assets.find((a) => a._id === nextAsset);
				const ownerId = currentAsset?.owner?._id || currentAsset?.owner;
				if (ownerId !== accountId) {
					nextAsset = null;
				}
			}
			return { ...prevForm, account: accountId, asset: nextAsset };
		});
		syncInjectedParam('accountId', accountId, consumedAccountId);
		clearFieldErrors(['account']);
	};

	const handleAssetChange = (assetId) => {
		setForm((prevForm) => {
			const selectedAsset = assets.find((a) => a._id === assetId);
			const ownerId = selectedAsset?.owner?._id || selectedAsset?.owner;
			return {
				...prevForm,
				asset: assetId,
				account: ownerId || prevForm.account,
			};
		});
		syncInjectedParam('propertyId', assetId, consumedAssetId);
		clearFieldErrors(['asset']);
	};

	const calculatedDates = useMemo(() => {
		if (form.type === 'Periodico' && form.startMonth && form.startYear && form.billingDay) {
			const month = parseInt(form.startMonth, 10) - 1;
			const year = parseInt(form.startYear, 10);
			const day = parseInt(form.billingDay, 10);

			if (!isNaN(month) && !isNaN(year) && !isNaN(day)) {
				const generationDate = createDate(year, month, day);
				generationDate.setHours(0, 0, 0, 0);

				const expirationDate = createDate(year, month + 2, day);
				expirationDate.setHours(0, 0, 0, 0);

				return {
					generationDate: !isNaN(generationDate.getTime()) ? generationDate : null,
					expirationDate: !isNaN(expirationDate.getTime()) ? expirationDate : null,
				};
			}
		}
		return { generationDate: null, expirationDate: null };
	}, [form.type, form.startMonth, form.startYear, form.billingDay]);

	const billMessages = useMemo(() => {
		if (form.type !== 'Periodico' || !form.startMonth || !form.startYear || !form.billingDay || !form.periodicity) {
			return null;
		}

		const startMonthNum = parseInt(form.startMonth, 10) - 1;
		const startYearNum = parseInt(form.startYear, 10);
		const billingDayNum = parseInt(form.billingDay, 10);
		const step = parseInt(form.periodicity, 10);

		if (isNaN(startMonthNum) || isNaN(startYearNum) || isNaN(billingDayNum) || isNaN(step) || step < 1) {
			return null;
		}

		const today = new Date();
		const currentIdx = today.getFullYear() * 12 + today.getMonth();
		const startIdx = startYearNum * 12 + startMonthNum;

		const formatMonthYear = (idx) => {
			const m = String((idx % 12) + 1).padStart(2, '0');
			const y = Math.floor(idx / 12);
			return `${m}/${y}`;
		};
		const formatDate = (d) => {
			const dd = String(d.getDate()).padStart(2, '0');
			const mm = String(d.getMonth() + 1).padStart(2, '0');
			return `${dd}/${mm}/${d.getFullYear()}`;
		};

		const periodsNow = [];
		if (startIdx > currentIdx) {
			periodsNow.push(startIdx);
		} else if (form.generateRetroactive) {
			for (let idx = startIdx; idx <= currentIdx; idx += step) {
				periodsNow.push(idx);
			}
		} else {
			const monthsDiff = currentIdx - startIdx;
			const steps = Math.floor(monthsDiff / step);
			periodsNow.push(startIdx + steps * step);
		}

		const lastNowIdx = periodsNow[periodsNow.length - 1];
		const nextGenIdx = lastNowIdx + step;
		const nextGenYear = Math.floor(nextGenIdx / 12);
		const nextGenMonth = nextGenIdx % 12;
		const nextGenDate = createDate(nextGenYear, nextGenMonth, billingDayNum);
		const nextExpDate = createDate(nextGenYear, nextGenMonth + 2, billingDayNum);

		const messages = [];
		if (periodsNow.length > 0) {
			messages.push(`Se generarán los siguientes gastos: ${periodsNow.map(formatMonthYear).join(', ')}.`);
		}
		if (nextGenDate && !isNaN(nextGenDate.getTime())) {
			messages.push(`Próxima generación: ${formatDate(nextGenDate)}.`);
		}
		if (nextExpDate && !isNaN(nextExpDate.getTime())) {
			messages.push(`Próximo vencimiento: ${formatDate(nextExpDate)}.`);
		}

		return messages.length > 0 ? messages : null;
	}, [form.type, form.startMonth, form.startYear, form.billingDay, form.periodicity, form.generateRetroactive]);

	const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

	const FIELD_LABELS = {
		name: t('field.name'),
		amount: t('field.amount'),
		account: t('entity.account'),
		asset: t('field.linkedAsset'),
		currency: t('field.currency'),
		startDate: t('form.billDate'),
		expirationDate: t('field.expirationDate'),
		startMonth: t('form.startMonth'),
		startYear: t('form.startYear'),
		periodicity: t('field.periodicity'),
		billingDay: t('field.billingDay'),
		payers: t('billPayers.title'),
	};

	const payersEnabled = isBillPayersEnabled();
	const estimatesEnabled = isBillEstimatesEnabled();
	// Bills store their own currency only once the ERP features are in use;
	// legacy deployments keep deriving it from the asset (field not sent).
	const sendsCurrency = payersEnabled || estimatesEnabled || isBillSourcesEnabled();

	const getFieldErrors = () => {
		const errors = {};
		if (!form.name.trim()) errors.name = REQUIRED_FIELD_MESSAGE;
		if (!form.amount) errors.amount = REQUIRED_FIELD_MESSAGE;
		// An instance edit renders only the basic fields, so validating the rest
		// would make the form impossible to submit.
		if (isInstanceScope) return errors;
		if (!form.account) errors.account = REQUIRED_FIELD_MESSAGE;
		if (!form.asset) errors.asset = REQUIRED_FIELD_MESSAGE;
		if (!form.currency) errors.currency = REQUIRED_FIELD_MESSAGE;
		if (payersEnabled && form.payers.length > 0) {
			const payerErrors = validatePayers(form.payers, { ownerAccount: form.account });
			if (payerErrors.length > 0) errors.payers = payerErrors.join(' ');
		}
		if (form.type === 'Eventual') {
			if (!form.startDate) errors.startDate = REQUIRED_FIELD_MESSAGE;
			if (!form.expirationDate) errors.expirationDate = REQUIRED_FIELD_MESSAGE;
		} else {
			if (!form.startMonth) errors.startMonth = REQUIRED_FIELD_MESSAGE;
			if (!form.startYear) errors.startYear = REQUIRED_FIELD_MESSAGE;
			if (!form.periodicity) errors.periodicity = REQUIRED_FIELD_MESSAGE;
			if (!form.billingDay) errors.billingDay = REQUIRED_FIELD_MESSAGE;
		}
		return errors;
	};

	const buildPayload = () => {
		if (isInstanceScope) {
			return {
				name: form.name,
				amount: Number(form.amount),
				folder: form.folder,
				observations: form.observations,
			};
		}

		let startDate = null;
		if (form.type === 'Periodico') {
			const month = parseInt(form.startMonth, 10) - 1;
			const year = parseInt(form.startYear, 10);
			const day = parseInt(form.billingDay, 10);
			startDate = createDate(year, month, day);
			startDate.setHours(0, 0, 0, 0);
		} else {
			startDate = form.startDate || null;
			if (startDate && !(startDate instanceof Date)) {
				startDate = new Date(startDate);
			}
		}

		return {
			name: form.name,
			amount: Number(form.amount),
			expirationDate: form.type === 'Periodico' && calculatedDates.expirationDate ? calculatedDates.expirationDate : (form.expirationDate || null),
			startDate,
			asset: form.asset,
			account: form.account,
			type: form.type,
			folder: form.folder,
			observations: form.observations,
			periodicity: form.periodicity || null,
			billingDay: form.type === 'Periodico' ? Number(form.billingDay) : undefined,
			partyType: form.partyType || undefined,
			bankAccount: form.bankAccount || null,
			generateRetroactive: form.type === 'Periodico' ? form.generateRetroactive === true : undefined,
			...(sendsCurrency ? { currency: form.currency || undefined } : {}),
			...(payersEnabled
				? { payers: form.payers.map((p) => ({ account: p.account, percentage: Number(p.percentage) })) }
				: {}),
			...(estimatesEnabled && form.type === 'Periodico' ? { amountMode: form.amountMode } : {}),
		};
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (loading || saving) {
			return;
		}

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			dispatch({
				type: 'SAVE_FAIL',
				payload: `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`,
			});
			return;
		}
		setFieldErrors({});

		dispatch({ type: 'SAVE_REQUEST' });
		try {
			const payload = buildPayload();
			let savedBillId = billId || initialData?._id;
			if (isInstanceScope) {
				await BillService.editInstance(savedBillId, payload);
				toast.success(t('msg.billUpdated'));
			} else if (isTemplateScope) {
				await BillService.editTemplate(savedBillId, payload);
				toast.success('Plantilla de gasto actualizada');
			} else if (isEditing) {
				await BillService.edit(savedBillId, payload);
				toast.success(t('msg.billUpdated'));
			} else {
				const result = await BillService.create(payload);
				savedBillId = result.data?.bill?._id || result.data?._id || savedBillId;
				toast.success(t('msg.billCreated'));
			}
			dispatch({ type: 'SAVE_SUCCESS' });
			if (onSaved) {
				onSaved(savedBillId);
			}
		} catch (ex) {
			const message = getError(ex);
			dispatch({ type: 'SAVE_FAIL', payload: message });
			console.error(message);
			toast.error(isEditing ? 'Error al actualizar gasto' : 'Error al crear gasto');
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? 'Guardar' : 'Agregar');
	const selectedAssetObj = form.asset ? (assets.find((a) => a._id === form.asset) || preloadedAsset) : null;
	const selectedAccountObj = form.account ? accounts.find((acc) => acc._id === form.account) : null;
	const selectedBankAccountObj = form.bankAccount ? bankAccounts.find((ba) => ba._id === form.bankAccount) : null;

	return (
		<Form id='bill-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos del gasto.' : 'Completá los datos para crear el gasto.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					<Button variant='primary' icon={isEditing ? <FaRegSave /> : <MdAdd />} type='submit' disabled={saving || loading}>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			{isTemplateScope && (
				<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-4 tw:py-3 tw:text-[13px] tw:text-ink-1'>
					Los cambios se aplicarán solo a gastos futuros. Los gastos ya generados no se modifican.
				</div>
			)}

			<Section
				title='Tipo de Gasto'
				description={isTypeLocked ? 'El tipo del gasto queda definido por el registro desde el que abriste el editor.' : 'Elegí si el gasto es periódico o eventual.'}
			>
				<div className='tw:inline-flex tw:overflow-hidden tw:rounded-[7px] tw:border tw:border-border-strong tw:bg-surface'>
					{BILL_TYPES.map((typeOption) => {
						const selected = form.type === typeOption;
						return (
							<button
								key={typeOption}
								type='button'
								className={`tw:inline-flex tw:h-[34px] tw:items-center tw:gap-2 tw:border-0 tw:px-3 tw:text-[13px] tw:font-medium tw:transition-colors ${
									selected ? 'tw:bg-accent tw:text-white' : 'tw:bg-surface tw:text-ink-2 hover:tw:bg-surface-2'
								} disabled:tw:cursor-not-allowed disabled:tw:opacity-60`}
								onClick={() => handleTypeChange(typeOption)}
								disabled={saving || isTypeLocked}
								aria-pressed={selected}
							>
								{typeOption}
								<RequiredMark />
							</button>
						);
					})}
				</div>
			</Section>

			<Section title='Datos generales'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='bill-name' label={t('field.name')} required>
						<Form.Control
							id='bill-name'
							className={controlClass}
							value={form.name}
							onChange={(event) => updateField('name', event.target.value)}
						/>
						<FieldError message={fieldErrors.name} />
					</FormField>
					<FormField id='bill-folder' label={t('field.folder')}>
						<Form.Control
							id='bill-folder'
							className={controlClass}
							value={form.folder}
							onChange={(event) => updateField('folder', event.target.value)}
						/>
					</FormField>
					<FormField id='bill-amount' label={t('field.amount')} required>
						<Form.Control
							id='bill-amount'
							type='number'
							step='any'
							min={0}
							placeholder='0.00'
							className={controlClass}
							value={form.amount}
							onChange={(event) => updateField('amount', event.target.value)}
						/>
						<FieldError message={fieldErrors.amount} />
					</FormField>
					<FormField id='bill-currency' label={t('field.currency')} required>
						<Select
							id='bill-currency'
							options={CURRENCIES}
							value={form.currency}
							onChange={(event) => updateField('currency', event.target.value)}
							placeholder={t('form.selectCurrency')}
							disabled={saving || isInstanceScope}
						/>
						<FieldError message={fieldErrors.currency} />
					</FormField>
				</div>
			</Section>

			{!isInstanceScope && (
				<Section title='Asociado a' description='Cuenta y activo a los que corresponde este gasto.'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='bill-account' label={t('entity.account')} required>
							<CustomTypeahead
								id='bill-account'
								labelKey={(option) => option?.name || option?.customId || ''}
								onChange={(selected) => handleAccountChange(selected && selected.length > 0 ? selected[0]._id : null)}
								options={accounts}
								selected={selectedAccountObj ? [selectedAccountObj] : []}
								placeholder={t('form.selectAssociatedAccount')}
								linkToCreate={`/AdminScreen/newAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nueva cuenta'
								disabled={saving || isAssetLocked}
								variant='entity'
							/>
							<FieldError message={fieldErrors.account} />
						</FormField>
						<FormField id='bill-asset' label={t('field.linkedAsset')} required>
							<CustomTypeahead
								id='bill-asset'
								labelKey={(option) => option?.customId || option?.name || ''}
								onChange={(selected) => handleAssetChange(selected && selected.length > 0 ? selected[0]._id : null)}
								options={assetOptions}
								selected={selectedAssetObj ? [selectedAssetObj] : []}
								placeholder={t('form.selectAsset')}
								linkToCreate={`/AdminScreen/newAsset?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nuevo activo'
								disabled={saving || isAssetLocked}
								variant='entity'
							/>
							<FieldError message={fieldErrors.asset} />
						</FormField>
						<FormField id='bill-party-type' label={t('field.billResponsible')}>
							<Select
								id='bill-party-type'
								options={[
									{ value: 'inquilino', label: t('form.partyType.tenant') },
									{ value: 'propietario', label: t('form.partyType.owner') },
								]}
								value={form.partyType}
								onChange={(event) => updateField('partyType', event.target.value)}
								placeholder={t('form.select')}
								disabled={saving}
							/>
						</FormField>
						<FormField id='bill-bank-account' label={t('form.paymentAccount')}>
							<CustomTypeahead
								id='bill-bank-account'
								labelKey={(ba) => (ba ? `${ba.bank} - ${ba.accountNumber} (${ba.owner}, ${ba.currency})` : '')}
								onChange={(selected) => {
									const nextId = selected && selected.length > 0 ? selected[0]._id : null;
									updateField('bankAccount', nextId);
									syncInjectedParam('bankAccountId', nextId, consumedBankAccountId);
								}}
								options={bankAccounts}
								selected={selectedBankAccountObj ? [selectedBankAccountObj] : []}
								placeholder={t('form.placeholder.searchBankAccount')}
								linkToCreate={`/AdminScreen/newBankAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nueva cuenta bancaria'
								disabled={saving}
								variant='entity'
							/>
						</FormField>
					</div>
				</Section>
			)}

			{payersEnabled && (
				<Section title={t('billPayers.title')} description={t('billPayers.description')}>
					<BillPayersEditor
						payers={form.payers}
						onChange={(next) => updateField('payers', next)}
						accounts={accounts}
						excludeAccount={form.account}
						amount={form.amount}
						currency={form.currency}
						disabled={saving}
						error={fieldErrors.payers}
					/>
				</Section>
			)}

			{!isInstanceScope && form.type === 'Eventual' && (
				<Section title='Fechas'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='bill-start-date' label={t('form.billDate')} required>
							<Form.Control
								id='bill-start-date'
								type='date'
								className={controlClass}
								value={toDateInputValue(form.startDate)}
								onChange={(event) => updateField('startDate', event.target.value ? new Date(event.target.value) : null)}
							/>
							<FieldError message={fieldErrors.startDate} />
						</FormField>
						<FormField id='bill-expiration-date' label={t('field.expirationDate')} required>
							<Form.Control
								id='bill-expiration-date'
								type='date'
								className={controlClass}
								value={toDateInputValue(form.expirationDate)}
								onChange={(event) => updateField('expirationDate', event.target.value ? new Date(event.target.value) : null)}
							/>
							<FieldError message={fieldErrors.expirationDate} />
						</FormField>
					</div>
				</Section>
			)}

			{!isInstanceScope && form.type === 'Periodico' && (
				<Section title='Periodicidad'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='bill-start-month' label={t('form.startMonth')} required>
							<Select
								id='bill-start-month'
								options={MONTH_OPTIONS}
								value={form.startMonth}
								onChange={(event) => updateField('startMonth', event.target.value)}
								placeholder={t('form.selectMonth')}
							/>
							<FieldError message={fieldErrors.startMonth} />
						</FormField>
						<FormField id='bill-start-year' label={t('form.startYear')} required>
							<Select
								id='bill-start-year'
								options={YEAR_OPTIONS}
								value={form.startYear}
								onChange={(event) => updateField('startYear', event.target.value)}
								placeholder={t('form.selectYear')}
							/>
							<FieldError message={fieldErrors.startYear} />
						</FormField>
						<FormField id='bill-periodicity' label={t('field.periodicity')} required>
							<Select
								id='bill-periodicity'
								options={periodicityOptions}
								value={form.periodicity}
								onChange={(event) => updateField('periodicity', event.target.value)}
								placeholder={t('form.select')}
							/>
							<FieldError message={fieldErrors.periodicity} />
						</FormField>
						<FormField id='bill-billing-day' label={t('field.billingDay')} required>
							<Form.Control
								id='bill-billing-day'
								className={controlClass}
								value={form.billingDay}
								onChange={(event) => updateField('billingDay', event.target.value)}
							/>
							<FieldError message={fieldErrors.billingDay} />
						</FormField>
						{estimatesEnabled && (
							<FormField id='bill-amount-mode' label={t('billEstimates.amountMode')} className='md:tw:col-span-2'>
								<Select
									id='bill-amount-mode'
									options={[
										{ value: 'fixed', label: t('billEstimates.mode.fixed') },
										{ value: 'estimated', label: t('billEstimates.mode.estimated') },
									]}
									value={form.amountMode}
									onChange={(event) => updateField('amountMode', event.target.value)}
									disabled={saving}
								/>
								<p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-ink-2'>{t('billEstimates.modeHelp')}</p>
							</FormField>
						)}
						<div className='md:tw:col-span-2'>
							{/* Hidden on a template edit: changing future generation settings
							    must never backfill bills for periods already past. */}
							{!isTemplateScope && (
								<Checkbox
									label={t('form.generateRetroactive')}
									checked={form.generateRetroactive}
									onChange={(event) => updateField('generateRetroactive', event.target.checked)}
									disabled={saving}
								/>
							)}
							{billMessages && (
								<div className='tw:mt-2 tw:text-[12.5px] tw:text-ink-2'>
									{billMessages.map((msg) => (
										<p key={msg} className='tw:m-0'>{msg}</p>
									))}
								</div>
							)}
						</div>
					</div>
				</Section>
			)}

			<Section title='Observaciones' description='Notas internas o información adicional del gasto.'>
				<FormField id='bill-observations' label={t('field.observations')}>
					<Form.Control
						id='bill-observations'
						as='textarea'
						rows={8}
						className={textareaClass}
						value={form.observations}
						onChange={(event) => updateField('observations', event.target.value)}
					/>
				</FormField>
			</Section>
		</Form>
	);
};

BillsEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	billId: PropTypes.string,
	initialData: PropTypes.object,
	scope: PropTypes.oneOf(['full', 'instance', 'template']),
	typeLocked: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
};

export default BillsEditor;
